/**
 * In-browser API mock for the `statement-download` prototype: a handful of
 * royalty statements to preview and download.
 *
 * The two newest statements have a revenue CSV that is still being prepared.
 * It becomes ready CSV_PREP_MS after the list first loads, so a reviewer can
 * watch the preview's Download menu or the row's actions menu change while it
 * is open. The clock lives in memory: reload the page to watch it again. The
 * two oldest are legacy statements, from before CSV exports: they never get one.
 *
 * Payees come from the contract-out-licensing prototype's fixtures, so both
 * prototypes show the same label.
 *
 * Scaffolding — delete with the rest of src/prototypes/statement-download/.
 */
import { API_BASE_URL } from "@/lib/api/core";
import { jsonResponse, paginated, registerDevMockRoutes } from "@/lib/dev-mode";
import type { RevenueCsvStatus, RoyaltyStatement, RoyaltyStatementStatus } from "@/types";
import { PAYEES } from "../contract-out-licensing/fixtures";

const CSV_PREP_MS = 30_000;

const PDF_URL = "/prototypes/statement-download/sample-statement.pdf";
const CSV_URL = "/prototypes/statement-download/sample-revenue.csv";

type SeedStatement = {
  id: string;
  statement_no: string;
  payee: string;
  period_start: string;
  period_end: string;
  statement_value: string;
  previous_balance: string;
  status: RoyaltyStatementStatus;
  /** Where the revenue CSV starts when the page loads; "preparing" turns "ready". */
  csv: RevenueCsvStatus;
};

const SEED: SeedStatement[] = [
  {
    id: "rs-nova-2026h1",
    statement_no: "RS-0012",
    payee: "payee-nova-skye",
    period_start: "2026-01-01",
    period_end: "2026-06-30",
    statement_value: "4820.40",
    previous_balance: "0.00",
    status: "draft",
    csv: "preparing",
  },
  {
    id: "rs-marcus-2026h1",
    statement_no: "RS-0011",
    payee: "payee-marcus-vale",
    period_start: "2026-01-01",
    period_end: "2026-06-30",
    statement_value: "1275.10",
    previous_balance: "38.20",
    status: "draft",
    csv: "preparing",
  },
  {
    id: "rs-hollow-2026h1",
    statement_no: "RS-0010",
    payee: "payee-hollow-pines",
    period_start: "2026-01-01",
    period_end: "2026-06-30",
    statement_value: "912.75",
    previous_balance: "0.00",
    status: "sent",
    csv: "ready",
  },
  {
    id: "rs-june-2025h2",
    statement_no: "RS-0009",
    payee: "payee-june-ashworth",
    period_start: "2025-07-01",
    period_end: "2025-12-31",
    statement_value: "2310.00",
    previous_balance: "0.00",
    status: "paid",
    csv: "ready",
  },
  {
    id: "rs-nova-2025h2",
    statement_no: "RS-0008",
    payee: "payee-nova-skye",
    period_start: "2025-07-01",
    period_end: "2025-12-31",
    statement_value: "3988.60",
    previous_balance: "0.00",
    status: "paid",
    csv: "ready",
  },
  {
    id: "rs-nova-2024h2",
    statement_no: "RS-0003",
    payee: "payee-nova-skye",
    period_start: "2024-07-01",
    period_end: "2024-12-31",
    statement_value: "2744.30",
    previous_balance: "0.00",
    status: "paid",
    csv: "unavailable",
  },
  {
    id: "rs-marcus-2024h2",
    statement_no: "RS-0002",
    payee: "payee-marcus-vale",
    period_start: "2024-07-01",
    period_end: "2024-12-31",
    statement_value: "640.85",
    previous_balance: "0.00",
    status: "paid",
    csv: "unavailable",
  },
];

/** When the list first loaded; not persisted, so a reload replays the preparing CSVs. */
let firstServedAt: number | null = null;

function csvStatus(seed: SeedStatement): RevenueCsvStatus {
  if (seed.csv !== "preparing") return seed.csv;
  firstServedAt ??= Date.now();
  return Date.now() - firstServedAt >= CSV_PREP_MS ? "ready" : "preparing";
}

function payload(seed: SeedStatement): RoyaltyStatement {
  const payee = PAYEES.find((p) => p.id === seed.payee)!;
  const balance = (Number(seed.statement_value) + Number(seed.previous_balance)).toFixed(2);
  const createdAt = `${seed.period_end}T12:00:00Z`;
  const csv = csvStatus(seed);
  return {
    id: seed.id,
    statement_no: seed.statement_no,
    payee: payee.id,
    payee_details: {
      id: payee.id,
      name: payee.name,
      legal_name: payee.legal_name,
      preferred_currency: payee.preferred_currency,
      country: payee.country,
      contact_email: payee.contact_email,
      is_active: true,
    },
    contract: null,
    contract_details: null,
    period_start: seed.period_start,
    period_end: seed.period_end,
    statement_value: seed.statement_value,
    previous_balance: seed.previous_balance,
    statement_balance: balance,
    meets_payment_threshold: Number(balance) >= 50,
    payment_threshold: "50.00",
    currency: "USD",
    file: PDF_URL,
    file_url: PDF_URL,
    revenue_csv: csv === "ready" ? CSV_URL : null,
    revenue_csv_status: csv,
    created_by: null,
    status: seed.status,
    created_at: createdAt,
    updated_at: createdAt,
  };
}

function statementHtml(statement: RoyaltyStatement): string {
  const lines = [
    ["Streaming", (Number(statement.statement_value) * 0.72).toFixed(2)],
    ["Downloads", (Number(statement.statement_value) * 0.08).toFixed(2)],
    ["Sync", (Number(statement.statement_value) * 0.2).toFixed(2)],
  ];
  return `<!doctype html><html><head><style>
    body { font-family: Inter, system-ui, sans-serif; color: #111; margin: 32px; font-size: 13px; }
    h1 { font-size: 20px; margin: 0 0 4px; }
    .muted { color: #666; }
    table { width: 100%; border-collapse: collapse; margin-top: 24px; }
    th, td { text-align: left; padding: 8px 0; border-bottom: 1px solid #e5e5e5; }
    td:last-child, th:last-child { text-align: right; }
    tfoot td { font-weight: 600; border-bottom: none; }
  </style></head><body>
    <h1>Royalty statement ${statement.statement_no}</h1>
    <div class="muted">${statement.payee_details.name} · ${statement.period_start} – ${statement.period_end}</div>
    <table>
      <thead><tr><th>Source</th><th>Amount (USD)</th></tr></thead>
      <tbody>${lines.map(([source, amount]) => `<tr><td>${source}</td><td>${amount}</td></tr>`).join("")}</tbody>
      <tfoot>
        <tr><td>Previous balance</td><td>${statement.previous_balance}</td></tr>
        <tr><td>Statement balance</td><td>${statement.statement_balance}</td></tr>
      </tfoot>
    </table>
  </body></html>`;
}

function basePath(): string {
  return new URL(API_BASE_URL, window.location.origin).pathname.replace(/\/$/, "");
}

registerDevMockRoutes((request, url) => {
  if (request.method !== "GET") return null;
  const path = url.pathname.slice(basePath().length).replace(/\/$/, "") || "/";

  if (path === "/revenue/royalty-statements") {
    const statuses = url.searchParams.get("status")?.split(",").filter(Boolean) ?? [];
    const rows = SEED.map(payload).filter(
      (statement) => statuses.length === 0 || statuses.includes(statement.status),
    );
    return jsonResponse(paginated(rows));
  }

  const match = path.match(/^\/revenue\/royalty-statements\/([^/]+)(\/html)?$/);
  const seed = match && SEED.find((s) => s.id === match[1]);
  if (!seed) return null;
  const statement = payload(seed);
  return jsonResponse(match[2] ? { html: statementHtml(statement) } : statement);
});
